import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuditTrailData, getFieldsIncluded } from "../util/getAuditTrailData";
import { getRevAndTraining, prepareCurrentSnapshot, uploadJsonToS3 } from "../util/AppUtils";
import { uniqueVersionIdRefs } from "../util/versionIdRefUtils";

const baseUrl = BASE_URL + "procedure-revision";

/** One in-flight GET per partRef/part_number — avoids duplicate HTTP when effects overlap or remount. */
const getProcedureRevisionsForPartInflight = new Map();

// 📌 Get Procedure Revisions grouped by purpose for a specific user (GET)
export const getRevisionsByUser = createAsyncThunk(
  "procedureRevision/getRevisionsByUser",
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/rev-by-user/${userId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching revisions by user"
      );
    }
  }
);

// 📌 Update Current Snapshot (POST - Helper for create/update)
export const updateCurrentSnapshot = createAsyncThunk(
  "procedureRevision/updateCurrentSnapshot",
  async (
    { partId, revisionId, partNumber, rev, currentStatus, type, revAndTrainingData },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const revNodes = await dispatch(
        getRevAllNodesWithRevInfo({
          partId,
          revisionId,
        })
      ).unwrap();

      let prepSnapshot = {
        ...revNodes, 
        partDescription:revNodes.part_description,
        ...revAndTrainingData,
        part_ref:partId,
        procedure_rev_ref:revisionId
      };

      prepSnapshot = JSON.parse(JSON.stringify(prepSnapshot))

      delete prepSnapshot.part_description

      console.log("prepSnapshot",prepSnapshot)
      const current_snapshot = await uploadJsonToS3(
        prepSnapshot,
        partNumber,
        rev
      );
      console.log("prepSnapshot",prepSnapshot)
      const current_updated_snapshot = await uploadJsonToS3(
        prepSnapshot,
        partNumber,
        rev + "current_updated_snapshot",
        true
      );

      const response = await axios.post(`${baseUrl}/update-current-snapshot`, {
        current_snapshot,
        current_updated_snapshot,
        procedure_revision_id: revisionId,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Error updating current snapshot"
      );
    }
  }
);

// 📌 Create Procedure Revision Maintianed inside the system (POST)
export const createProcedureRevision = createAsyncThunk(
  "procedureRevision/create",
  async ({ revisionData, version_content }, { dispatch, rejectWithValue }) => {
    const password = revisionData?.audit_trail_data?.hash;
    const isPasswordRequired = revisionData?.audit_trail_data?.is_password_required !== false;
    const revData = { ...revisionData };
    delete revData.hash;
    const ad = await getAuditTrailData({
      action: "CREATE_PROCEDURE_REVISION",
      ...revisionData?.audit_trail_data,
    },false);

    // Exclude signature from version_content
    const { signature: _, ...revisionDataWithoutSignature } = revisionData || {};
    const { signature: __, ...versionContentWithoutSignature } = version_content || {};
    
    ad.version_content = {
      ...revisionDataWithoutSignature,
      requested_by: localStorage.getItem("user_name"),
      password_bullets: password ? password.length : 0,
      ...versionContentWithoutSignature,
      ...version_content,

      action_to_show: `Created ${version_content.procedure_number} Rev ${revisionData.rev}`,
      current_status: revisionData.status_changed_to,
      copy_type:"system",
      signature: ad.signature ? ad.signature : "",
      ...getFieldsIncluded(),
    };
    delete ad.version_content.audit_trail_data;
    delete ad.version_content.node_indexing;
    const revisionNodesSnapshot = await uploadJsonToS3(ad.version_content.revisionNodes || [],"created","rev",true)
    delete ad.version_content.revisionNodes;
    delete revisionData.audit_trail_data;

    const revisionAndTraininglogs = await getRevAndTraining(dispatch,revisionData?.part_ref)
    const snapshot_url = await uploadJsonToS3({
      ...revisionData.node_indexing,
      version_content:{
        ...ad.version_content,
        main_entry:undefined,
        revision:ad.version_content.rev,
        operation:"bulk_audit_trail",
        ...revisionAndTraininglogs
      }
    },ad.version_content.partNumber,ad.version_content.rev+`${ad.version_content.item_code_type.toUpperCase()}_revision_created`,true)


    const payload = {
      ...(isPasswordRequired && password ? { password: password } : {}),
      ...revisionData,
      audit_trail_data: { ...ad },
      item_code_number: ad.version_content.procedure_number,
      item_code_type: ad.version_content.type,
    };

    payload.audit_trail_data.version_content.revisionNodesSnapshot = revisionNodesSnapshot

    Object.entries(revisionData.node_indexing).forEach(([key, value]) => {
      payload.node_indexing[key]={
        title:value.title,
        previous_content_snapshot:snapshot_url,
        new_content_snapshot:snapshot_url,
      }
    });
    
    try {
      const response = await axios.post(`${baseUrl}/create`, payload);
      
      // Check if response indicates failure (success: false)
      if (response.data && response.data.success === false) {
        const errorMsg = response.data.message || response.data.error || "Operation failed";
        console.error("========== REDUX SLICE: Server returned success: false ==========");
        console.error("Error message:", errorMsg);
        return rejectWithValue(errorMsg);
      }
      
      // Additional check: if response doesn't have expected success structure, validate it
      if (response.data && !response.data.results) {
        // If there's a message or error field, treat it as an error
        if (response.data.message || response.data.error) {
          const errorMsg = response.data.message || response.data.error || "Operation failed";
          console.error("========== REDUX SLICE: Missing results with error message ==========");
          console.error("Error message:", errorMsg);
          return rejectWithValue(errorMsg);
        }
      }
      
      // Check HTTP status code - if it's not 200-299, treat as error
      if (response.status < 200 || response.status >= 300) {
        const errorMsg = response.data?.message || response.data?.error || `HTTP ${response.status} error`;
        console.error("========== REDUX SLICE: Non-success HTTP status ==========");
        console.error("Status:", response.status, "Error:", errorMsg);
        return rejectWithValue(errorMsg);
      }
      
      // Check for password errors even if success is true (defensive check)
      if (response.data) {
        const responseStr = JSON.stringify(response.data).toLowerCase();
        if (responseStr.includes("incorrect password") || 
            responseStr.includes("wrong password") || 
            responseStr.includes("invalid password") ||
            responseStr.includes("password incorrect") ||
            responseStr.includes("password is incorrect") ||
            responseStr.includes("password error") ||
            responseStr.includes("authentication failed") ||
            responseStr.includes("password validation failed")) {
          const errorMsg = response.data.message || response.data.error || "Incorrect password";
          console.error("========== REDUX SLICE: PASSWORD ERROR DETECTED ==========");
          console.error("Password error detected in response:", errorMsg);
          console.error("Response string contains password error keywords");
          return rejectWithValue(errorMsg);
        }
      }

      await dispatch(
        updateCurrentSnapshot({
          partId: response.data.results.part_ref,
          revisionId: response.data.results.procedure_revision_id,
          partNumber: ad.version_content.partNumber,
          rev: ad.version_content.rev,
          currentStatus: ad.version_content.current_status,
          type: ad.version_content.type,
        })
      ).unwrap();
      
      return response.data;
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Error creating revision";
      console.error("Error creating revision:", errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// 📌 Create Procedure Revision Maintianed Outside The System(MOS)
export const createProcedureRevisionMOS = createAsyncThunk(
  "procedureRevision/createMOS",
  async ({ revisionData, version_content }, { rejectWithValue }) => {
    const revData = { ...revisionData };
    delete revData.hash;
    const ad = await getAuditTrailData({
      action: "CREATE_PROCEDURE_REVISION_MOS",
      comment:version_content.revision_details,
      ...version_content,
    });

    const payload = {
      audit_trail_data: {
        ...ad,
        version_content: {
          action_to_show:`Created ${ad.procedure_number} Revision ${ad.rev}`,
          ...ad.version_content,
          rev:ad.rev,
          date_released:ad.date_released,
          revision_details:ad.revision_details,
          reference:ad.reference,
        },
      },
      ...revisionData,

      password: "TEST PASSWORD TO WORK",
    };
    try {
      const response = await axios.post(`${baseUrl}/create`, payload);
      // Check if response indicates failure (success: false)
      if (response.data && response.data.success === false) {
        return rejectWithValue(response.data.message || response.data.error || "Operation failed");
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.response?.data?.error || "Error creating revision"
      );
    }
  }
);

// 📌 Update Procedure Revision (PUT)
export const updateProcedureRevision = createAsyncThunk(
  "procedureRevision/update",
  async ({ id, data, audit_trail_data }, { rejectWithValue }) => {
    const ad = await getAuditTrailData(audit_trail_data);
    const payload = {
      ...data,
      audit_trail_data: ad,
    };
    try {
      const response = await axios.put(`${baseUrl}/update/${id}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating revision");
    }
  }
);

export const getRevAllNodes = createAsyncThunk(
  "step/getRevAllNodes",
  async ({ partId, revisionId,otherParams, is_deleted, withoutToken }, { rejectWithValue }) => {
    try {
      let endPoint = "/get-procedure-nodes"
      if(withoutToken){
        endPoint = "/get-all-rev-node"
      }
      const response = await axios.get(
        `${baseUrl}${endPoint}?part_ref=${partId}&procedure_rev_ref=${revisionId}${otherParams ? `&${otherParams}` : ""}${is_deleted ? `&is_delete=${true}` : ""}`
      );
      
      return response.data?.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching steps for section"
      );
    }
  }
);

export const getRevAllNodesWithStatus = createAsyncThunk(
  "step/getRevAllNodesWithStatus",
  async ({ partId, revisionId,otherParams, is_deleted }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-procedure-nodes?part_ref=${partId}&procedure_rev_ref=${revisionId}${otherParams ? `&${otherParams}` : ""}${is_deleted ? `&is_delete=${true}` : ""}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching steps for section"
      );
    }
  }
);

export const getRevAllNodesWithRevInfo = createAsyncThunk(
  "step/getRevAllNodesWithRevInfo",
  async ({ partId, revisionId,otherParams, is_deleted }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-procedure-nodes?part_ref=${partId}&procedure_rev_ref=${revisionId}${otherParams ? `&${otherParams}` : ""}${is_deleted ? `&is_delete=${true}` : ""}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching steps for section"
      );
    }
  }
);

export const getVersionIdsByLtreePath = createAsyncThunk(
  "procedureRevision/getVersionIdsByLtreePath",
  async ({ ltree_path }, { rejectWithValue }) => {
    try {
      const normalizedLtreePath = String(ltree_path || "").trim();
      const response = await axios.post(`${baseUrl}/get-version-ids`, {
        ltree_path: normalizedLtreePath,
      });
      const payload = response?.data;
      const candidates = [
        payload?.version_ids,
        payload?.versionIds,
        payload?.data?.version_ids,
        payload?.data?.versionIds,
        payload?.results?.version_ids,
        payload?.results?.versionIds,
        payload?.data,
        payload?.results,
      ];
      const picked = candidates.find((item) => Array.isArray(item));
      if (!Array.isArray(picked)) {
        return [];
      }
      const normalizedIds = uniqueVersionIdRefs(picked);
      return normalizedIds;
    } catch (error) {
      console.error("[getVersionIdsByLtreePath] request failed", error);
      return rejectWithValue(
        error.response?.data || "Error fetching version ids",
      );
    }
  },
);

// 📌 Get All Procedure Revisions (GET)
export const getAllProcedureRevisions = createAsyncThunk(
  "procedureRevision/getAll",
  async ({ limit = 10, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?limit=${limit}&offset=${offset}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching revisions"
      );
    }
  }
);

// 📌 Fetch All Revisions with Search/Filter/Sort (GET)
export const fetchAllRevisions = createAsyncThunk(
  "procedureRevision/fetchAll",
  async ({ searchQuery }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?${searchQuery}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching revisions"
      );
    }
  }
);


export const fetchRevsonSearch = createAsyncThunk(
  "procedureRevision/fetchRevsonSearch",
  async ({ searchQuery }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all-search?${searchQuery}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching revisions"
      );
    }
  }
);

export const getProcedureRevisionsForPart = createAsyncThunk(
  "procedureRevision/getForPart",
  async (
    { partId, part_number, userRef, isCheckTrainingCompleted },
    { rejectWithValue },
  ) => {
    const key = partId ? `id:${partId}` : `pn:${part_number}`;
    const existing = getProcedureRevisionsForPartInflight.get(key);
    if (existing) {
      return existing;
    }

    const requestPromise = (async () => {
      try {
        const liveModeQueryParams = new URLSearchParams();
        if (userRef) liveModeQueryParams.set("user_ref", userRef);
        if (isCheckTrainingCompleted !== undefined) {
          liveModeQueryParams.set(
            "is_check_training_completed",
            String(Boolean(isCheckTrainingCompleted)),
          );
        }
        const liveModeQuerySuffix = liveModeQueryParams.toString()
          ? `&${liveModeQueryParams.toString()}`
          : "";
        const response = partId
          ? await axios.get(
              `${baseUrl}/get-all?limit=100000&offset=0&partRef=${partId}${liveModeQuerySuffix}`,
            )
          : await axios.get(
              `${baseUrl}/get-all-rev?limit=100000&offset=0&part_number=${part_number}`,
            );
        return response.data;
      } catch (error) {
        return rejectWithValue(
          error.response?.data || "Error fetching revisions",
        );
      } finally {
        getProcedureRevisionsForPartInflight.delete(key);
      }
    })();

    getProcedureRevisionsForPartInflight.set(key, requestPromise);
    return requestPromise;
  },
);

// Separate action for part mentions to avoid overwriting main revisionData
export const getPartMentionsRevisionsForPart = createAsyncThunk(
  "procedureRevision/getPartMentionsForPart",
  async ({ partId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?limit=100000&offset=0&partRef=${partId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching revisions"
      );
    }
  }
);

export const getRevisionsForRestore = createAsyncThunk(
  "procedureRevision/getForRestore",
  async ({ versionId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}version/get-rev-version?version_id=${versionId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching revisions"
      );
    }
  }
);

export const getRevisionsForPart = createAsyncThunk(
  "procedureRevision/getRevisionsForPart",
  async ({ partId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-part-procedure?limit=1000000&offset=0${
          partId ? "&part_id=" + partId : ""
        }`
      );
      return { results: response.data.results, partId };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching revisions"
      );
    }
  }
);

export const getTrainingLogForPart = createAsyncThunk(
  "procedureRevision/getTrainingLogForPart",
  async ({ partId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-training-log?part_ref=${partId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching revisions"
      );
    }
  }
);

// 📌 Get Procedure Revision by ID (GET)
export const getProcedureRevisionById = createAsyncThunk(
  "procedureRevision/getById",
  async (id, { rejectWithValue }) => {
    try {
      // Add timestamp to prevent browser caching and ensure fresh data
      const timestamp = new Date().getTime();
      const response = await axios.get(`${baseUrl}/get-by-id/${id}?_t=${timestamp}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching revision");
    }
  }
);

// 📌 Delete Procedure Revision (DELETE)
export const deleteProcedureRevision = createAsyncThunk(
  "procedureRevision/delete",
  async (id, { rejectWithValue }) => {
    try {
      const resp = await axios.delete(`${baseUrl}/delete/${id}`);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting revision");
    }
  }
);

// 📌 Separate action for get pending task count
export const getPendingTasksCount = createAsyncThunk(
  "procedureRevision/getPendingTasksCount",
  async ({ partId, revisionId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}procedure-task/count-released-and-after/${partId}/${revisionId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching revisions"
      );
    }
  }
);

// 🛠 Procedure Revision Slice
const procedureRevisionSlice = createSlice({
  name: "procedureRevision",
  initialState: {
    revisions: [],
    revisionData: [],
    noRevisions:false,
    partMentionsRevisionData: [], // Separate state for part mentions popup to avoid conflicts
    revisionsForPart: {},
    trainingLogs: [],
    loading: false,
    error: null,
    revisionId: null,
    allRevisionNodes: [],
    revisionsByUserData: null,
    pagination: {},
    pendingTasksCountData: [],
    currentStatus:null,
    item_code_type:null,
    revAllInfo: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getPendingTasksCount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPendingTasksCount.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingTasksCountData = action.payload.data;
      })
      .addCase(getPendingTasksCount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(createProcedureRevision.pending, (state) => {
        state.loading = true;
      })
      .addCase(createProcedureRevision.fulfilled, (state, action) => {
        state.loading = false;

        state.revisionId =
          action.payload.results.tableOfProcedures.procedure_revision_ref;
        state.revisions.push(action.payload.results);
      })
      .addCase(createProcedureRevision.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(createProcedureRevisionMOS.pending, (state) => {
        state.loading = true;
      })
      .addCase(createProcedureRevisionMOS.fulfilled, (state, action) => {
        state.loading = false;

        state.revisionId =
          action.payload.results.tableOfProcedures.procedure_revision_ref;
        state.revisions.push(action.payload.results);
      })
      .addCase(createProcedureRevisionMOS.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(updateProcedureRevision.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProcedureRevision.fulfilled, (state, action) => {
        state.loading = false;
        state.revisions = state.revisions.map((rev) =>
          rev.id === action.payload.id ? action.payload : rev
        );
      })
      .addCase(updateProcedureRevision.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(getAllProcedureRevisions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllProcedureRevisions.fulfilled, (state, action) => {
        state.loading = false;
        state.revisions = action.payload.results;
      })
      .addCase(getAllProcedureRevisions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(fetchAllRevisions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllRevisions.fulfilled, (state, action) => {
        state.loading = false;
        state.revisions = action.payload.results;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchAllRevisions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(fetchRevsonSearch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRevsonSearch.fulfilled, (state, action) => {
        state.loading = false;
        state.revisions = action.payload.results;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchRevsonSearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(getRevisionsForRestore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRevisionsForRestore.fulfilled, (state, action) => {
        state.loading = false;
        state.revisions = action.payload.results;
      })
      .addCase(getRevisionsForRestore.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(getTrainingLogForPart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTrainingLogForPart.fulfilled, (state, action) => {
        state.loading = false;
        state.trainingLogs = action.payload.results;
      })
      .addCase(getTrainingLogForPart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(getProcedureRevisionsForPart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProcedureRevisionsForPart.fulfilled, (state, action) => {
        state.loading = false;
        state.revisionData = action.payload.results;
        state.noRevisions  = action.payload.results.length === 0;
      })
      .addCase(getProcedureRevisionsForPart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(getPartMentionsRevisionsForPart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPartMentionsRevisionsForPart.fulfilled, (state, action) => {
        state.loading = false;
        state.partMentionsRevisionData = action.payload.results;
      })
      .addCase(getPartMentionsRevisionsForPart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(getRevAllNodes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRevAllNodes.fulfilled, (state, action) => {
        state.loading = false;
        state.allRevisionNodes = action.payload;
      })
      .addCase(getRevAllNodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      
      .addCase(getRevAllNodesWithStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRevAllNodesWithStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.allRevisionNodes = action.payload.data; // Keeping consistency in Redux state if used elsewhere
        state.currentStatus = action.payload.procedure_status;
        state.item_code_type = action.payload.item_code_type;
      })
      .addCase(getRevAllNodesWithStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(getRevAllNodesWithRevInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRevAllNodesWithRevInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.revAllInfo = action.payload; // Keeping consistency in Redux state if used elsewhere
      })
      .addCase(getRevAllNodesWithRevInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(getRevisionsForPart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRevisionsForPart.fulfilled, (state, action) => {
        state.loading = false;

        for (const row of action.payload.results) {
          if (action.payload.partId === (row.part_id || row.part_ref)) {
            state.revisionsForPart = row;
            break;
          }
        }
      })
      .addCase(getRevisionsForPart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(getRevisionsByUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRevisionsByUser.fulfilled, (state, action) => {
        state.loading = false;
        state.revisionsByUserData = action.payload;
      })
      .addCase(getRevisionsByUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(deleteProcedureRevision.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteProcedureRevision.fulfilled, (state, action) => {
        state.loading = false;
        state.revisions = state.revisions.filter(
          (rev) => rev.id !== action.payload
        );
      })
      .addCase(deleteProcedureRevision.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      });
  },
});

export default procedureRevisionSlice.reducer;
